import { faStar } from "@fortawesome/pro-regular-svg-icons"
import { faFloppyDisk, faRedo, faTrashXmark, faUndo } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Fab } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { deepEquals } from "app/tools/common"
import { messageSVC } from "message/service"
import React from "react"
import { connect } from "react-redux"
import { DarkModeSwitch } from "ui/components/DarkModeSwitch"
import { FlexBox } from "ui/components/FlexBox"
import { IUIConfiguration } from "ui/configuration/model"
import { uiConfigurationRPO } from "ui/configuration/repository"
import { setIsSavingUiConfig, setIsUiConfigDarkPreview } from "ui/configuration/tools/common"
import {
  canRedoUiConfigChange,
  canUndoUiConfigChange,
  redoUiConfigChange,
  resetUiConfigChanges,
  undoUiConfigChange
} from "ui/configuration/tools/undo-redo"
import { uiSVC } from "ui/service"
import { isPhoneScreen } from "ui/tools/responsive"

interface IUiConfigHeaderProps {
  isDarkPreview: boolean
  uiConfig: IUIConfiguration
  tempUiConfig: IUIConfiguration
}

interface IUiConfigHeaderConnectProps {
  isSaving: boolean
  isPhone: boolean
}

const UiConfigHeaderFn = React.memo(
  ({
    isSaving,
    isDarkPreview,
    uiConfig,
    tempUiConfig,
    isPhone
  }: IUiConfigHeaderProps & IUiConfigHeaderConnectProps) => {
    const hasChanged = !deepEquals(uiConfig, tempUiConfig)
    return (
      <FlexBox
        jc="space-between"
        wrap
        sx={{
          width: "100%",
          position: "sticky",
          zIndex: 2,
          top: "-.1rem",
          left: 0,
          p: "1rem",
          pt: ".5rem",
          pb: ".5rem",
          boxShadow: theme => theme.palette.boxShadow,
          backgroundColor: theme => theme.palette.background.default
        }}>
        <DarkModeSwitch
          isDark={isDarkPreview}
          setIsDark={newIsDarkPreview => setIsUiConfigDarkPreview(newIsDarkPreview)}
          label={translate(
            isDarkPreview ? "admin.ui-configuration.dark.test.on" : "admin.ui-configuration.dark.test.off"
          )}
        />
        <FlexBox gap>
          <Fab
            color="primary"
            aria-label="undo"
            title={translate("admin.ui-configuration.undo.tooltip")}
            size="small"
            disabled={!canUndoUiConfigChange() || isSaving}
            sx={{ "& .icon": { height: "1.1rem" } }}
            onClick={() => undoUiConfigChange()}>
            <FontAwesomeIcon icon={faUndo} className="icon" />
          </Fab>
          <Fab
            color="primary"
            aria-label="redo"
            title={translate("admin.ui-configuration.redo.tooltip")}
            size="small"
            disabled={!canRedoUiConfigChange() || isSaving}
            sx={{ "& .icon": { height: "1.1rem" } }}
            onClick={() => redoUiConfigChange()}>
            <FontAwesomeIcon icon={faRedo} className="icon" />
          </Fab>
          <Fab
            color="primary"
            aria-label="default"
            title={translate("admin.ui-configuration.default.tooltip")}
            size="small"
            disabled={isSaving}
            sx={{ "& .icon": { height: "1.1rem" } }}
            onClick={() =>
              messageSVC.confirmMessage({
                message: translate("admin.ui-configuration.default.message"),
                title: translate("admin.ui-configuration.default.title"),
                onSuccess: async () => {
                  setIsSavingUiConfig(true)
                  try {
                    messageSVC.displayWaitingOverlay(translate("label.saving"))
                    const defaultConfig = await uiConfigurationRPO.getDefaultConfiguration()
                    const newConfig = await uiConfigurationRPO.saveConfiguration(defaultConfig)
                    uiSVC.setDarkThemeActive(uiSVC.isDarkThemeActive(), newConfig)
                    messageSVC.success(translate("admin.ui-configuration.default.success"))
                    resetUiConfigChanges(defaultConfig)
                    messageSVC.closeWaitingOverlay()
                  } catch (error) {
                    console.error(error)
                    messageSVC.closeWaitingOverlay()
                    setIsSavingUiConfig(false)
                    messageSVC.error(translate("admin.ui-configuration.default.error"))
                  }
                }
              })
            }>
            <FontAwesomeIcon icon={faStar} className="icon" />
          </Fab>
          <Fab
            color="primary"
            aria-label="cancel"
            title={translate("admin.ui-configuration.cancel.tooltip")}
            size="small"
            disabled={!hasChanged || isSaving}
            sx={{ "& .icon": { height: "1.1rem" } }}
            onClick={() =>
              messageSVC.confirmMessage({
                message: translate("admin.ui-configuration.cancel.message"),
                title: translate("admin.ui-configuration.cancel.title"),
                onSuccess: () => resetUiConfigChanges()
              })
            }>
            <FontAwesomeIcon icon={faTrashXmark} className="icon" />
          </Fab>
          <Fab
            color="primary"
            aria-label="apply"
            variant={isPhone ? undefined : "extended"}
            title={translate("admin.ui-configuration.save.tooltip")}
            size="small"
            disabled={!hasChanged || isSaving}
            sx={{
              "textTransform": "initial",
              "& .icon": {
                mr: isPhone ? undefined : "1rem",
                height: "1.1rem"
              }
            }}
            onClick={() =>
              messageSVC.confirmMessage({
                message: translate("admin.ui-configuration.save.message"),
                title: translate("admin.ui-configuration.save.title"),
                onSuccess: () => {
                  setIsSavingUiConfig(true)
                  messageSVC.displayWaitingOverlay(translate("label.saving"))
                  uiConfigurationRPO
                    .saveConfiguration(tempUiConfig)
                    .then(newConfig => {
                      uiSVC.setDarkThemeActive(uiSVC.isDarkThemeActive(), newConfig)
                      messageSVC.success(translate("admin.ui-configuration.save.success"))
                    })
                    .catch(error => {
                      console.error(error)
                      setIsSavingUiConfig(false)
                      messageSVC.error(translate("admin.ui-configuration.save.error"))
                    })
                    .finally(() => messageSVC.closeWaitingOverlay())
                }
              })
            }>
            <FontAwesomeIcon icon={faFloppyDisk} className="icon" />
            {!isPhone && translate("admin.ui-configuration.save.button")}
          </Fab>
        </FlexBox>
      </FlexBox>
    )
  }
)

export const UiConfigHeader = connect(
  (state: IRootState): IUiConfigHeaderConnectProps => ({
    isSaving: state.ui.isSavingConfig,
    isPhone: isPhoneScreen(state)
  })
)(UiConfigHeaderFn)
